import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useLocation, Link } from "react-router-dom";
import './SideQuest.css';
import { createPost, createPostForPlan } from '../../actions/postAction';
import { decreasetokens } from '../../actions/userAction';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import TokenPopup from './TokenPopup';
import TypeOfNote from './TypeOfNote';

const LOCAL_STORAGE_KEY = 'sideQuestDraft';
const MAX_CHARS = 1500;

const SideQuest = () => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams();

  const [step, setStep] = useState(1);
  const [tokenCount, setTokenCount] = useState(1);
  const [content, setContent] = useState('');
  const [selectedOption, setSelectedOption] = useState('digicoins');
  const [btnloading, setbtnloading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showNoti, setshowNoti] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showCongrats, setShowCongrats] = useState(false);
  const [createdPostId, setCreatedPostId] = useState(null);
  const [createdPlanId, setCreatedPlanId] = useState(null);
  const [congratsType, setCongratsType] = useState(null);
  const [targetDays, setTargetDays] = useState(0);
  const [displayDays, setDisplayDays] = useState(0);

  // Draft autosave
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) setContent(saved);
  }, []);

  useEffect(() => {
    setIsSaving(true);
    const t = setTimeout(() => {
      if (content.trim()) {
        localStorage.setItem(LOCAL_STORAGE_KEY, content);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
      setIsSaving(false);
    }, 1000);
    return () => clearTimeout(t);
  }, [content]);

  useEffect(() => {
    if (user.country && user.country !== "IN") setshowNoti(true);
  }, [user.country]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (content.length > MAX_CHARS) {
      toast.error(`Exceeds ${MAX_CHARS} characters.`);
      return;
    }
    if (selectedOption === "digicoins" && user.tokens === 0) {
      toast.error("Insufficient D-credits.", { autoClose: false });
      return;
    }
    const data = { note: content, method: selectedOption, category: "side-quest" };
    try {

      setbtnloading(true);
      let res;
      if (planId) {
        res = await dispatch(createPostForPlan({ ...data, planId }));
        setCreatedPlanId(planId);
        setCongratsType('plan');
        const endDate = res?.plan?.endDate || res?.endDate || null;
        let days = 365;
        if (endDate) days = Math.max(0, Math.ceil((new Date(endDate) - new Date()) / 86400000));
        setTargetDays(days);
        setDisplayDays(0);
        setShowCongrats(true);
      } else {
        res = await dispatch(createPost(data));
        setCreatedPostId(res._id);
        setCongratsType('note');
        setShowCongrats(true);
      }
      if (res && selectedOption === "digicoins") await dispatch(decreasetokens(1));
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setContent("");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setbtnloading(false);
    }
  };

  // Congrats — plan
  useEffect(() => {
    if (!showCongrats || congratsType !== 'plan') return;
    if (targetDays === 0) {
      const t = setTimeout(() => { setShowCongrats(false); navigate(`/dashboard/year-plan/${createdPlanId}`); }, 1200);
      return () => clearTimeout(t);
    }
    const steps = Math.max(1, Math.ceil(2000 / 40));
    const increment = Math.max(1, Math.ceil(targetDays / steps));
    let current = 0;
    const iv = setInterval(() => {
      current += increment;
      if (current >= targetDays) {
        setDisplayDays(targetDays);
        clearInterval(iv);
        setTimeout(() => { setShowCongrats(false); navigate(`/dashboard/year-plan/${createdPlanId}`); }, 800);
      } else {
        setDisplayDays(current);
      }
    }, 40);
    return () => clearInterval(iv);
  }, [showCongrats, congratsType, targetDays, createdPlanId, navigate]);

  // Congrats — note
  useEffect(() => {
    if (!showCongrats || congratsType !== 'note') return;
    const t = setTimeout(() => {
      setShowCongrats(false);
      if (createdPostId) navigate(`/dashboard/note-details/${createdPostId}`);
    }, 1500);
    return () => clearTimeout(t);
  }, [showCongrats, congratsType, createdPostId, navigate]);

  const isNextDisabled = content.trim().length === 0 || content.length > MAX_CHARS;

  return (
    <div className="sq-container">
      <Helmet><title>New Side Quest — dmyor</title></Helmet>

      {/* ── STEP 1: WRITE ── */}
      {step === 1 && (
        <>
          {/* HEADER */}
          <div className="sq-header">
            <span className="sq-eyebrow">Side Quest</span>
            <h2 className="sq-title">What are you going after?</h2>
            <p className="sq-subtitle">
            Quests you take on just because you want to. A record of you making what you want a reality.
            </p>

          </div>

          {/* EDITOR */}
          <div className="sq-editor-card">
            <div className="sq-editor-header">
              <span className="sq-editor-pill">◈ side quest</span>
              <span className={`sq-autosave ${isSaving ? "saving" : ""}`}>
                {isSaving ? "Saving draft…" : "Draft saved"}
              </span>
            </div>

            <textarea
              className="sq-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Learning to drive this month..."
            />

            <div className="sq-editor-footer">
              <span className="sq-hint">Write it as if it's already done.</span>
              <span className={`sq-char-counter ${content.length >= MAX_CHARS * 0.9 ? "limit-warning" : ""}`}>
                {content.length} / {MAX_CHARS}
              </span>
            </div>
          </div>

          {/* FOOTER */}
          <div className="sq-footer">
            <button
              className="sq-next-btn"
              type="button"
              onClick={() => setStep(2)}
              disabled={isNextDisabled}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: POST ── */}
      {step === 2 && (
        <div className="plan-step-container">
          <TypeOfNote
            kind="sideQuest"
            user={user}
            creditsRequired={1}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            handleBack={() => setStep(1)}
            handlePost={handlePost}
            btnloading={btnloading}
            showNoti={showNoti}
            setshowNoti={setshowNoti}
            setShowPopup={setShowPopup}
          />
        </div>
      )}

      {/* ── TOKEN POPUP ── */}
      {showPopup && (
        <TokenPopup
          tokenCount={tokenCount}
          updateTokenCount={setTokenCount}
          closePopup={() => setShowPopup(false)}
        />
      )}

      {/* ── CONGRATS OVERLAY ── */}
      {showCongrats && (
        <div className="congrats-overlay">
          <div className="congrats-modal">
            {congratsType === 'plan' ? (
              <>
                <div className="congrats-badge">Side Quest Added</div>
                <h2 className="congrats-title">Quest planted.</h2>
                <p className="congrats-sub">Days remaining in this plan</p>
                <div className="congrats-counter">{displayDays}</div>
                <div className="congrats-actions">
                  <button
                    className="congrats-btn primary"
                    onClick={() => { setShowCongrats(false); navigate(`/dashboard/year-plan/${createdPlanId}`); }}
                  >
                    Back to Plan
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="congrats-badge">Side Quest Posted</div>
                <div className="congrats-emoji">◈</div>
                <h2 className="congrats-title">Quest is live.</h2>
                <p className="congrats-sub">Go get it.</p>
                <div className="congrats-actions">
                  <button
                    className="congrats-btn primary"
                    onClick={() => { setShowCongrats(false); if (createdPostId) navigate(`/dashboard/note-details/${createdPostId}`); }}
                  >
                    View Quest
                  </button>
                  <button className="congrats-btn secondary" onClick={() => setShowCongrats(false)}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default SideQuest;
